import { useTranslation } from "react-i18next";

import type { CcPackageSummary } from "@/api/code-companion";
import { useCcPackages } from "@/hooks/useCodeCompanion";
import { CC_MONO_MUTED } from "@/lib/cc-trace-tokens";

/**
 * Escolher o pacote de dentro do Trace ou do Evidence (S-064 onda 7, correção).
 *
 * A primeira versão escondia as duas tabs até haver um pacote em foco, com o argumento
 * de que "tab que abre vazia ensina o usuário a ignorá-la". O argumento estava certo e a
 * conclusão errada: esconder faz a barra de navegação **mudar de forma** debaixo de quem
 * usa — as tabs aparecem do nada depois de clicar num card, e as vizinhas mudam de lugar.
 *
 * Uma tab com seletor não está vazia: ela oferece a escolha que falta. A navegação fica
 * estável, e quem chega direto no Trace descobre o caminho em vez de topar com um beco.
 *
 * A ordem é a mesma do board — o que está em andamento primeiro, porque é o que alguém
 * abre o Trace para ver.
 */

/** Peso por status: em execução primeiro, terminado por último. */
const PESO: Record<string, number> = {
  in_progress: 0,
  blocked: 1,
  ready_for_deploy: 2,
  approved: 3,
  backlog: 4,
  done: 5,
};

export function ordenarParaEscolha(pacotes: CcPackageSummary[]): CcPackageSummary[] {
  return [...pacotes].sort((a, b) => {
    const pa = PESO[a.status] ?? 9;
    const pb = PESO[b.status] ?? 9;
    return pa === pb ? a.devclass.localeCompare(b.devclass) : pa - pb;
  });
}

export default function PackagePicker({
  projectId,
  titulo,
  onPick,
}: {
  projectId: string;
  titulo: string;
  onPick: (p: CcPackageSummary) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useCcPackages(projectId);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        {t("Loading packages...")}
      </div>
    );
  }

  const pacotes = ordenarParaEscolha(data?.items ?? []);

  if (!pacotes.length) {
    return (
      <div className="rounded-xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        {t("This project has no package yet.")}
      </div>
    );
  }

  return (
    <div data-testid="package-picker" className="flex flex-col gap-3">
      <div>
        <h2
          className="m-0"
          style={{
            fontFamily: "var(--font-brand)",
            fontWeight: 700,
            fontSize: "20px",
            letterSpacing: "-0.015em",
          }}
        >
          {titulo}
        </h2>
        <p className="mt-1 text-[12.5px]" style={{ color: "var(--exed-ink-500)" }}>
          {t("Pick a package to follow.")}
        </p>
      </div>

      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {pacotes.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onPick(p)}
              className="w-full rounded-lg bg-card px-3 py-2.5 text-left hover:bg-muted/40"
              style={{ border: "1px solid var(--exed-card-border)" }}
            >
              <div className="flex items-baseline gap-2">
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "var(--exed-ink-900)",
                  }}
                >
                  {p.devclass}
                </span>
                <div className="flex-1" />
                <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", color: CC_MONO_MUTED }}>
                  {p.status}
                </span>
              </div>
              {/* Com denominador, como no hero: a fatia sozinha passa por total. */}
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--exed-ink-500)" }}>
                {t("{{n}} of {{total}} objects in development scope", {
                  n: p.in_scope_count,
                  total: p.object_count,
                })}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
